import type { PanderrBlueprint } from "./service-business-v1-adapter.js";
import { normalizeBlueprint } from "../normalize/normalize-blueprint.js";
import { buildDesignBrief } from "../design-rules/build-design-brief.js";
import {
  selectDesignFamily,
  type DesignFamilyId,
} from "../design-rules/select-design-family.js";
import { renderModernBusiness } from "./modern-business/render-modern-business.js";

const FAMILY_ORDER: DesignFamilyId[] = [
  "modern-blue-v1",
  "modern-sage-v1",
  "modern-soft-pink-v1",
  "modern-navy-v1",
  "modern-charcoal-v1",
];

const FAMILY_LABELS: Record<DesignFamilyId, string> = {
  "modern-blue-v1": "Blue",
  "modern-sage-v1": "Sage",
  "modern-soft-pink-v1": "Soft Pink",
  "modern-navy-v1": "Navy",
  "modern-charcoal-v1": "Charcoal",
};

const COMMON_HEADER_OVERRIDES = `
.sb-header>.sb-shell{width:min(var(--sb-shell),calc(100% - 24px))}
.sb-header-inner{gap:clamp(18px,2.5vw,36px)}
.sb-brand{display:flex;align-items:center;justify-content:flex-start;flex:0 1 clamp(180px,24vw,330px);min-width:150px;max-width:330px;min-height:64px;margin-left:0!important;overflow:visible}
.sb-brand img[data-panderr-role="logo"]{display:block;width:auto;max-width:100%!important;max-height:78px!important;object-fit:contain;object-position:left center;margin-left:0;margin-right:auto;transform-origin:left center}
.sb-header--logo-small .sb-brand img[data-panderr-role="logo"]{max-height:58px!important}
.sb-header--logo-medium .sb-brand img[data-panderr-role="logo"]{max-height:68px!important}
.sb-header--logo-large .sb-brand img[data-panderr-role="logo"]{max-height:78px!important}
.sb-navigation{margin-left:auto}
.sb-hero-card{position:relative;overflow:hidden}
.sb-hero-background-image[data-panderr-role="hero-background"]{position:absolute!important;inset:0!important;width:100%!important;height:100%!important;max-width:none!important;object-fit:cover;margin:0!important;padding:0!important;border:0!important;border-radius:0!important;box-shadow:none!important;z-index:0}
.sb-hero-inner,.sb-hero-overlay{position:relative;z-index:1}
@media(max-width:920px){.sb-brand{flex-basis:min(48vw,280px);max-width:min(48vw,280px)}}
@media(max-width:640px){.sb-header>.sb-shell{width:calc(100% - 20px)}.sb-brand{min-width:110px;max-width:210px;min-height:54px}.sb-brand img[data-panderr-role="logo"]{max-height:58px!important}}
`;

const VISUAL_OVERRIDES = `
body.panderr-family-visual{--sb-radius:8px;--sb-radius-small:2px;--sb-shell:1380px;background:var(--sb-surface)}
body.panderr-family-visual .sb-header{background:var(--sb-surface);backdrop-filter:none}
body.panderr-family-visual .sb-header-inner{min-height:94px}
body.panderr-family-visual .sb-hero{padding:0;background:var(--sb-surface)}
body.panderr-family-visual .sb-hero>.sb-shell{width:100%;max-width:none}
body.panderr-family-visual .sb-hero-card{min-height:min(84vh,900px);border:0;border-radius:0;box-shadow:none}
body.panderr-family-visual .sb-hero-inner{align-items:end;padding:clamp(54px,8vw,112px)}
body.panderr-family-visual .sb-hero-copy{max-width:900px}
body.panderr-family-visual .sb-hero-copy h1{font-size:clamp(3.6rem,8vw,8.5rem);line-height:.88;max-width:1000px}
body.panderr-family-visual .sb-hero-overlay{background:linear-gradient(0deg,color-mix(in srgb,var(--sb-secondary) 78%,transparent),transparent 72%);opacity:.78}
body.panderr-family-visual .sb-section{padding:clamp(82px,10vw,150px) 0}
body.panderr-family-visual .sb-section-heading-wrap{grid-template-columns:minmax(0,.8fr) minmax(300px,1.2fr)}
body.panderr-family-visual .sb-card-grid{gap:48px 24px}
body.panderr-family-visual .sb-card{min-height:0;border:0;background:transparent;border-radius:0}
body.panderr-family-visual .sb-card:hover{transform:none;box-shadow:none;border-color:transparent}
body.panderr-family-visual .sb-card-media{aspect-ratio:4/5;border-radius:2px}
body.panderr-family-visual .sb-card-content{padding:20px 0 0}
body.panderr-family-visual .sb-card-icon{display:none}
body.panderr-family-visual .sb-card h3{font-size:1.55rem}
body.panderr-family-visual .sb-feature-grid{grid-template-columns:minmax(0,1.2fr) minmax(360px,.8fr)}
body.panderr-family-visual .sb-feature-media{border-radius:2px;box-shadow:none}
body.panderr-family-visual .sb-cta-card{border-radius:2px;box-shadow:none}
body.panderr-family-visual .sb-footer{padding-top:96px}
`;

const EDITORIAL_OVERRIDES = `
body.panderr-family-editorial{--sb-radius:0px;--sb-radius-small:0px;--sb-shell:1120px;background:var(--sb-surface)}
body.panderr-family-editorial h1,body.panderr-family-editorial h2,body.panderr-family-editorial h3{font-family:Georgia,'Times New Roman',serif;font-weight:500}
body.panderr-family-editorial .sb-header{position:static;background:var(--sb-surface);backdrop-filter:none}
body.panderr-family-editorial .sb-header-inner{min-height:76px}
body.panderr-family-editorial .sb-navigation a{font-weight:600}
body.panderr-family-editorial .sb-hero{padding:44px 0 84px;background:var(--sb-surface)}
body.panderr-family-editorial .sb-hero-card{min-height:540px;border:0;border-radius:0;box-shadow:none;background:var(--sb-surface-muted)}
body.panderr-family-editorial .sb-hero-inner{padding:clamp(44px,7vw,86px) 54% clamp(44px,7vw,86px) clamp(34px,6vw,72px)}
body.panderr-family-editorial .sb-hero-background-image{left:auto!important;right:0!important;width:48%!important;height:100%!important;object-fit:cover}
body.panderr-family-editorial .sb-hero-overlay{display:none}
body.panderr-family-editorial .sb-hero--image-background-overlay{color:var(--sb-text)}
body.panderr-family-editorial .sb-hero--image-background-overlay .sb-section-label{color:var(--sb-primary)}
body.panderr-family-editorial .sb-hero--image-background-overlay .sb-hero-lead,body.panderr-family-editorial .sb-hero--image-background-overlay .sb-hero-paragraphs{color:var(--sb-muted)}
body.panderr-family-editorial .sb-hero-copy h1{font-size:clamp(3rem,5.7vw,5.8rem);line-height:.98;letter-spacing:-.045em}
body.panderr-family-editorial .sb-button{border-radius:0;background:var(--sb-primary)}
body.panderr-family-editorial .sb-section{padding:clamp(66px,8vw,108px) 0}
body.panderr-family-editorial .sb-section h2{font-size:clamp(2.5rem,4.4vw,4.25rem);line-height:1.04;letter-spacing:-.04em}
body.panderr-family-editorial .sb-section-heading-wrap{grid-template-columns:minmax(0,.9fr) minmax(320px,1.1fr);border-bottom:1px solid var(--sb-line);padding-bottom:34px}
body.panderr-family-editorial .sb-card-grid{grid-template-columns:1fr!important;gap:0;border-top:1px solid var(--sb-line)}
body.panderr-family-editorial .sb-card{min-height:0;display:grid;grid-template-columns:88px minmax(0,1fr);border:0;border-bottom:1px solid var(--sb-line);border-radius:0;background:transparent}
body.panderr-family-editorial .sb-card:hover{transform:none;box-shadow:none}
body.panderr-family-editorial .sb-card-media{grid-column:1/-1;aspect-ratio:16/7}
body.panderr-family-editorial .sb-card-icon{padding:28px 20px 0 0;align-items:flex-start}
body.panderr-family-editorial .sb-card-content{padding:26px 0 32px}
body.panderr-family-editorial .sb-feature-media,body.panderr-family-editorial .sb-cta-card{border-radius:0;box-shadow:none}
body.panderr-family-editorial .sb-footer{padding-top:68px}
@media(max-width:760px){body.panderr-family-editorial .sb-hero-inner{padding:34px 24px 380px}body.panderr-family-editorial .sb-hero-background-image{top:auto!important;left:0!important;width:100%!important;height:340px!important}}
`;

const PANDERR_BLUE_OVERRIDES = `
body.panderr-family-blue{--sb-primary:#6f93dc;--sb-secondary:#172033;--sb-accent:#b5d1ff;--sb-surface:#fffdf9;--sb-surface-muted:#f5f8ff;--sb-line:#dfe7f5;--sb-radius:28px;--sb-radius-small:18px;--sb-shell:1180px;background:#fffdf9;color:#172033}
body.panderr-family-blue .sb-header{background:rgba(255,253,249,.92);border-bottom:1px solid rgba(181,209,255,.42);backdrop-filter:blur(16px)}
body.panderr-family-blue .sb-navigation a:hover{color:#5279c5}
body.panderr-family-blue .sb-hero{padding:clamp(74px,9vw,120px) 0;background:radial-gradient(circle at 85% 15%,#b5d1ff 0,transparent 30%),linear-gradient(135deg,#fffdf9,#fff 62%)}
body.panderr-family-blue .sb-hero-card{border:1px solid rgba(111,147,220,.2);border-radius:36px;background:linear-gradient(145deg,rgba(255,255,255,.88),rgba(245,248,255,.82));box-shadow:0 28px 72px rgba(55,83,139,.16)}
body.panderr-family-blue .sb-hero-overlay{background:linear-gradient(90deg,rgba(23,32,51,.72),rgba(23,32,51,.1));opacity:.72}
body.panderr-family-blue .sb-hero-copy h1{letter-spacing:-.055em}
body.panderr-family-blue .sb-button{border-radius:999px;background:linear-gradient(135deg,#b5d1ff,#dce8ff);color:#172033;box-shadow:0 12px 28px rgba(111,147,220,.22)}
body.panderr-family-blue .sb-section{padding:clamp(76px,8vw,112px) 0}
body.panderr-family-blue .sb-section:nth-of-type(even){background:linear-gradient(180deg,#fffdf9,#f5f8ff)}
body.panderr-family-blue .sb-card{border:1px solid rgba(111,147,220,.18);border-radius:28px;background:rgba(255,255,255,.88);box-shadow:0 18px 46px rgba(55,83,139,.09)}
body.panderr-family-blue .sb-card:hover{transform:translateY(-4px);box-shadow:0 24px 54px rgba(55,83,139,.14)}
body.panderr-family-blue .sb-card-icon{background:#e7efff;color:#5279c5;border-radius:16px}
body.panderr-family-blue .sb-feature-media{border-radius:30px;box-shadow:0 24px 56px rgba(55,83,139,.14)}
body.panderr-family-blue .sb-cta-card{border-radius:32px;background:linear-gradient(135deg,#b5d1ff,#e7eeff);color:#172033;box-shadow:0 24px 60px rgba(55,83,139,.17)}
body.panderr-family-blue .sb-footer{background:#172033}
`;

let forcedFamily: DesignFamilyId | undefined;
let lastFamily: DesignFamilyId = "modern-blue-v1";

function familyFromQuery(): DesignFamilyId | undefined {
  if (typeof window === "undefined") return undefined;

  const value = new URLSearchParams(window.location.search).get("design");

  return value === "blue"
    ? "modern-blue-v1"
    : value === "sage"
      ? "modern-sage-v1"
      : value === "soft-pink" || value === "pink"
        ? "modern-soft-pink-v1"
        : value === "navy"
          ? "modern-navy-v1"
          : value === "charcoal"
            ? "modern-charcoal-v1"
            : undefined;
}

function queryValue(familyId: DesignFamilyId): string {
  return familyId === "modern-sage-v1"
    ? "sage"
    : familyId === "modern-soft-pink-v1"
      ? "soft-pink"
      : familyId === "modern-navy-v1"
        ? "navy"
        : familyId === "modern-charcoal-v1"
          ? "charcoal"
          : "blue";
}

function modernThemeOverrides(familyId: DesignFamilyId): string {
  const theme =
    familyId === "modern-sage-v1"
      ? {
          primary: "#667d42",
          secondary: "#273021",
          accent: "#a9b98d",
          surface: "#ffffff",
          mutedSurface: "#f1f4eb",
          line: "#dde4d5",
          text: "#20271d",
          muted: "#687063",
        }
      : familyId === "modern-soft-pink-v1"
        ? {
            primary: "#d98e86",
            secondary: "#3a2d2d",
            accent: "#efb8b1",
            surface: "#ffffff",
            mutedSurface: "#fcf0ee",
            line: "#efd8d4",
            text: "#2a2323",
            muted: "#746766",
          }
        : familyId === "modern-navy-v1"
          ? {
              primary: "#193354",
              secondary: "#10233d",
              accent: "#91a8c2",
              surface: "#ffffff",
              mutedSurface: "#edf2f7",
              line: "#dce3eb",
              text: "#172033",
              muted: "#647081",
            }
          : familyId === "modern-charcoal-v1"
            ? {
                primary: "#292929",
                secondary: "#202020",
                accent: "#aaa49b",
                surface: "#ffffff",
                mutedSurface: "#f1f0ed",
                line: "#e1ded8",
                text: "#202020",
                muted: "#6c6964",
              }
            : {
                primary: "#3474dc",
                secondary: "#172033",
                accent: "#9fc0ef",
                surface: "#ffffff",
                mutedSurface: "#edf4ff",
                line: "#dce7f7",
                text: "#172033",
                muted: "#667085",
              };

  return `
    body.panderr-family-modern {
      --sb-primary: ${theme.primary};
      --sb-secondary: ${theme.secondary};
      --sb-accent: ${theme.accent};
      --sb-surface: ${theme.surface};
      --sb-surface-muted: ${theme.mutedSurface};
      --sb-line: ${theme.line};
      --sb-text: ${theme.text};
      --sb-muted: ${theme.muted};
      --sb-radius: 28px;
      --sb-radius-small: 18px;
      --sb-shell: 1180px;

      background: ${theme.surface};
      color: ${theme.text};
    }

    body.panderr-family-modern .sb-header {
      background: rgba(255, 255, 255, .94);
      border-bottom: 1px solid ${theme.line};
      backdrop-filter: blur(16px);
    }

    body.panderr-family-modern .sb-header-inner {
      min-height: 92px;
    }

    body.panderr-family-modern .sb-navigation a {
      color: ${theme.text};
      font-weight: 700;
    }

    body.panderr-family-modern .sb-navigation a:hover {
      color: ${theme.primary};
    }

    body.panderr-family-modern .sb-hero {
      padding: clamp(72px, 8vw, 118px) 0;
      background:
        linear-gradient(
          135deg,
          ${theme.surface},
          ${theme.mutedSurface}
        );
    }

    body.panderr-family-modern .sb-hero-card {
      overflow: hidden;
      border: 1px solid ${theme.line};
      border-radius: 34px;
      background: ${theme.surface};
      box-shadow: 0 28px 64px rgba(24, 32, 48, .09);
    }

    body.panderr-family-modern .sb-hero-inner {
      padding: clamp(42px, 6vw, 76px);
    }

    body.panderr-family-modern .sb-hero-copy h1 {
      max-width: 780px;
      font-size: clamp(3.1rem, 6.5vw, 6.5rem);
      line-height: .94;
      letter-spacing: -.055em;
    }

    body.panderr-family-modern .sb-hero-lead,
    body.panderr-family-modern .sb-hero-paragraphs {
      color: ${theme.muted};
    }

    body.panderr-family-modern .sb-section-label {
      color: ${theme.primary};
    }

    body.panderr-family-modern .sb-button {
      border-radius: 999px;
      background: ${theme.primary};
      color: #ffffff;
      box-shadow: none;
    }

    body.panderr-family-modern .sb-button:hover {
      filter: brightness(.94);
    }

    body.panderr-family-modern .sb-section {
      padding: clamp(72px, 8vw, 112px) 0;
    }

    body.panderr-family-modern .sb-section:nth-of-type(even) {
      background: ${theme.mutedSurface};
    }

    body.panderr-family-modern .sb-card {
      border: 1px solid ${theme.line};
      border-radius: 24px;
      background: ${theme.surface};
      box-shadow: 0 14px 36px rgba(24, 32, 48, .06);
    }

    body.panderr-family-modern .sb-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 20px 44px rgba(24, 32, 48, .1);
    }

    body.panderr-family-modern .sb-card-icon {
      border-radius: 14px;
      background: ${theme.mutedSurface};
      color: ${theme.primary};
    }

    body.panderr-family-modern .sb-feature-media {
      overflow: hidden;
      border-radius: 28px;
      box-shadow: 0 22px 50px rgba(24, 32, 48, .09);
    }

    body.panderr-family-modern .sb-cta-card {
      border-radius: 30px;
      background: ${theme.secondary};
      color: #ffffff;
      box-shadow: none;
    }

    body.panderr-family-modern .sb-footer {
      background: ${theme.secondary};
      color: #ffffff;
    }
  `;
}

function applyFamily(html: string, familyId: DesignFamilyId): string {
  const className = "panderr-family-modern";

  const familyOverrides = modernThemeOverrides(familyId);

  return html
    .replace("</style>", `${COMMON_HEADER_OVERRIDES}${familyOverrides}</style>`)
    .replace(
      '<a class="sb-brand" href="#top">',
      '<a class="sb-brand" data-panderr-component="header-brand" data-panderr-role="logo-container" href="#top">',
    )
    .replace(
      /<img src="([^"]+)" alt="([^"]*)">/,
      '<img src="$1" alt="$2" data-panderr-role="logo" data-panderr-component="logo" data-panderr-logo="true">',
    )
    .replace(
      /<section\s+class="sb-section sb-hero /,
      '<section data-panderr-component="hero" data-panderr-role="hero" class="sb-section sb-hero ',
    )
    .replace(
      '<img\n        class="sb-hero-background-image"',
      '<img\n        class="sb-hero-background-image"\n        data-panderr-role="hero-background"\n        data-panderr-component="hero-background"',
    )
    .replace(
      "<body>",
      `<body class="${className}" data-design-family="${familyId}" data-design-label="${FAMILY_LABELS[familyId]}">`,
    );
}

export const designFamilyBrowserBridge = {
  renderBlueprint(blueprint: PanderrBlueprint) {
    const normalized = normalizeBlueprint(blueprint).blueprint;

    const selection = selectDesignFamily(
      normalized,
      buildDesignBrief(normalized),
    );

    const familyId = forcedFamily ?? familyFromQuery() ?? selection.familyId;

    const rendered = renderModernBusiness(normalized, familyId);

    lastFamily = familyId;

    return {
      ...rendered,
      designFamilySelection: selection,
    };
  },

  renderDocument(blueprint: PanderrBlueprint) {
    return this.renderBlueprint(blueprint).html;
  },

  setDesignFamily(familyId?: DesignFamilyId) {
    forcedFamily = familyId;
    if (familyId) lastFamily = familyId;
    return forcedFamily;
  },

  cycleDesignFamily() {
    const index = FAMILY_ORDER.indexOf(lastFamily);
    const next = FAMILY_ORDER[(index + 1) % FAMILY_ORDER.length];
    forcedFamily = next;
    lastFamily = next;
    return next;
  },

  getCurrentDesignFamily() {
    return lastFamily;
  },

  getDesignFamilyLabel(familyId: DesignFamilyId = lastFamily) {
    return FAMILY_LABELS[familyId];
  },

  getNextDesignFamily() {
    const index = FAMILY_ORDER.indexOf(lastFamily);
    return FAMILY_ORDER[(index + 1) % FAMILY_ORDER.length];
  },

  getQueryValue(familyId: DesignFamilyId = lastFamily) {
    return queryValue(familyId);
  },
};

declare global {
  interface Window {
    PanderrDesignFamilies?: typeof designFamilyBrowserBridge;
  }
}

if (typeof window !== "undefined") {
  window.PanderrDesignFamilies = designFamilyBrowserBridge;
  (window as any).PanderrServiceBusinessV1 = designFamilyBrowserBridge;
}
